import { LogoFull } from '@ahamove/icons';
import { type PaymentMethod, PaymentMethodCode } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import {
  Avatar,
  Box,
  Button,
  Card,
  Center,
  Drawer,
  Image,
  Stack,
  Text,
  Title,
  SimpleGrid,
  rem,
  Popover,
  Group,
  type CardProps,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { QRCodeCanvas } from 'qrcode.react';
import { type ReactNode, useMemo } from 'react';
import LottieAnimation from './lottie-animation';
import successAnimationData from '@/assets/data/success-animation.json';
import { ROUTES } from '@/config/router';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useRouter } from '@/navigation';

type Props = CardProps & {
  title: string | ReactNode;
  amount: number;
  paymentMethod: PaymentMethod | null;
  qrCodeValue?: string;
  qrCodeUrl?: string;
  status?: string;
  onClose?: () => void;
  onPaymentSuccess?: () => void;
};

export default function QrCodeInfo({
  title,
  amount,
  paymentMethod,
  qrCodeValue,
  qrCodeUrl = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP89OVHPQAJHQNfV6AmqAAAAABJRU5ErkJggg==' /* transparent */,
  status,
  onClose,
  onPaymentSuccess,
  ...cardProps
}: Props) {
  const tPayment = useTranslations('payment');
  const router = useRouter();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      router.replace(`${ROUTES.home}`);
    }
  };

  const paymentMethodLogo = useMemo(() => {
    return paymentMethod?.icon_url ?? null;
  }, [paymentMethod]);

  return (
    <Card p={rem(24)} {...cardProps}>
      <Stack justify="center" align="center" pt="xs" gap={rem(4)}>
        <Text ta="center" size="sm" fw={500}>
          {title}
        </Text>
        <Title c="primary.6" order={2}>
          {formatCurrency(amount)}
        </Title>
        <Stack pos="relative" justify="center" align="center" mt="sm">
          {status === 'success' ? (
            <LottieAnimation
              animationData={successAnimationData}
              height="290px"
              width="290px"
            />
          ) : (
            <Stack gap={0}>
              {paymentMethodLogo ? (
                <Center
                  pos="relative"
                  mx="auto"
                  p={rem(8)}
                  bg="white"
                  style={{
                    bottom: rem(-24),
                    zIndex: 10,
                  }}
                >
                  <Avatar
                    radius="sm"
                    src={paymentMethodLogo}
                    size={40}
                    alt={paymentMethod?.code}
                  />
                </Center>
              ) : (
                <Center
                  pos="relative"
                  w={160}
                  h={28}
                  mx="auto"
                  px={rem(8)}
                  bg="white"
                  style={{
                    bottom: rem(-12),
                    zIndex: 10,
                  }}
                >
                  <LogoFull className="size-full" />
                </Center>
              )}

              <Card
                p="xl"
                withBorder
                shadow="none"
                radius="md"
                styles={{
                  root: {
                    borderWidth: 2,
                    borderColor: 'var(--mantine-color-secondary-5)',
                  },
                }}
              >
                {qrCodeValue ? (
                  <QRCodeCanvas value={qrCodeValue} size={256} />
                ) : (
                  <Box pos="relative" w={256 + 48} h={256 + 48} m={-24}>
                    <Image
                      src={qrCodeUrl}
                      width="100%"
                      height="100%"
                      fallbackSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP89OVHPQAJHQNfV6AmqAAAAABJRU5ErkJggg=="
                      alt="qr-code"
                      style={{ objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Card>
            </Stack>
          )}

          {status === 'success' ? (
            <Text ta="center" size="md" c="green.5" fw={600}>
              Thanh toán thành công
            </Text>
          ) : (
            <Text ta="center">
              {paymentMethod?.code === PaymentMethodCode.Vnpay.toLowerCase()
                ? tPayment('scan_form_hint')
                : 'Hãy quét mã này để thanh toán'}
            </Text>
          )}
        </Stack>

        <QrCodeInfoCloseAction onClose={handleClose} />
      </Stack>
    </Card>
  );
}

function QrCodeInfoCloseAction({ onClose }: { onClose?: () => void }) {
  const tCommon = useTranslations('common');
  const tActions = useTranslations('common.actions');
  const tPayment = useTranslations('payment');
  const isMobile = useIsMobile();

  const [opened, { open, close }] = useDisclosure(false);

  if (isMobile) {
    return (
      <>
        <Drawer
          zIndex={999}
          offset={8}
          position="bottom"
          overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
          radius="md"
          opened={opened}
          onClose={close}
          withCloseButton={false}
          title={tPayment('are_you_sure_you_want_to_cancel_the_payment')}
          styles={{
            content: {
              height: 'auto',
            },
          }}
        >
          <SimpleGrid cols={2} spacing="xs">
            <Button
              fullWidth
              onClick={close}
              variant="subtle"
              color="neutral"
              size="md"
              mih={rem(48)}
            >
              {tCommon('no')}
            </Button>
            <Button
              fullWidth
              color="red"
              size="md"
              mih={rem(48)}
              onClick={onClose}
            >
              {tCommon('yes')}
            </Button>
          </SimpleGrid>
        </Drawer>
        <Button
          mt="xl"
          color="default"
          variant="light"
          size="md"
          mih={rem(48)}
          fullWidth
          onClick={open}
        >
          {tActions('close')}
        </Button>
      </>
    );
  }

  return (
    <Popover
      width="target"
      position="bottom"
      trapFocus
      shadow="md"
      opened={opened}
    >
      <Popover.Target>
        <Button
          mt="xl"
          color="default"
          size="md"
          mih={rem(48)}
          fullWidth
          variant="light"
          onClick={open}
        >
          {tActions('close')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Text size="sm">
          {tPayment('are_you_sure_you_want_to_cancel_the_payment')}
        </Text>
        <Group justify="flex-end" wrap="nowrap" gap="xs" mt="md">
          <Button
            miw={96}
            onClick={close}
            variant="subtle"
            color="neutral"
            size="md"
          >
            {tCommon('no')}
          </Button>
          <Button miw={96} color="red" size="md" onClick={onClose}>
            {tCommon('yes')}
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
}
